<template>
  <div class="workerInfo">
    <p v-html="$t('workPermit.plsProvide')" />
    <ShowNotice
      :noticeContent="NoticeOnWorkerBadgesEN"
      :noticeContentCN="NoticeOnWorkerBadgesCN"
      :title="$t('workPermit.noticeOnWorkerBadges')"
      :expand="!isEngineer"
    />
    <div
      v-if="showOperate"
      class="workerInfo_operate"
    >
      <button
        v-if="!isEditing"
        class="btn--darkblue"
        @click="changeEditStatus"
      >{{ $t('action.edit') }}</button>
      <button
        v-if="isEditing"
        class="btn--orange"
        @click="save"
      >{{ $t('action.save') }}</button>
      <button
        v-if="isEditing"
        class="btn--darkblue"
        @click="cancel"
      >{{ $t('action.cancel') }}</button>
    </div>
    <table class="workerInfo_table">
      <thead>
        <tr>
          <th
            v-for="(header, index) in tableHeaders"
            :key="index"
            @click="sortTable(header)"
          >
            <span :class="header.className">{{ header.name }}</span>
            <span class="workerInfo_triangle_box">
              <i
                v-if="sortingAttr === header.attr"
                :class="[reverse ? 'triangle-down' : 'triangle-up']"
              />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in parsedWorkInfo"
          :key="item.id"
          @paste="onRowPaste"
        >
          <td class="index">
            {{ index + 1 }}
          </td>
          <template v-for="fullnameObj of getFullnameSorts">
            <td :key="fullnameObj.value">
              <InputField
                :readonly="!isEditing"
                :showColon="false"
                :showFlag="isEditing"
                :value="item[fullnameObj.value]"
                :required="isEditing"
                @updateValue="val => updateField(index, fullnameObj.value, val)"
              />
            </td>
          </template>
          <td class="cardNum">
            <InputField
              :readonly="!isEditing"
              :showColon="false"
              :showFlag="isEditing"
              :inputTip="$t('hint.inputTip.doNotOverXChars', [18])"
              :pattern="/^.{1,18}$/"
              :value="item.cardNum"
              :required="isEditing"
              @updateValue="val => updateField(index, 'cardNum', val)"
            />
          </td>
          <td class="cardExpiryDate">
            <span v-if="!isEditing">{{  item.cardExpiryDate  }}</span>
            <DatePicker
              v-else
              format="DD MMM YYYY"
              :clearButton="true"
              :calendarButton="true"
              calendarButtonIcon="fa fa-calendar"
              :value="item.cardExpiryDate"
              @selected="val => updateField(index, 'cardExpiryDate', val)"
              style="width: 100%;"
              :disabled="{ to: yesterday }"
            />
          </td>
          <td class="phone">
            <InputField
              :readonly="!isEditing"
              :showColon="false"
              :showFlag="isEditing"
              :value="item.phone"
              @updateValue="val => updateField(index, 'phone', val)"
            />
          </td>
          <td class="remarks">
            <InputField
              :readonly="!isEditing"
              :showColon="false"
              :showFlag="isEditing"
              :value="item.remarks"
              @updateValue="val => updateField(index, 'remarks', val)"
            />
            <i
              v-if="isEditing && workerInfo.length > 1"
              class="fa-times fa"
              @click="deleteWorker(index)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-if="isEditing"
      class="workerInfo_addWorker"
      @click="addWorkInfo"
    >
      {{ $t('workPermit.addWorker') }}
    </div>
    <div class="workerInfo_docs">
      <HkldDropzone
        v-if="canEditAttachment"
        :metaData="{ uploadOnTop: true }"
        :sourceId="docSourceId"
      />
      <EditAttachments
        :canDelete="true"
        :showEditPencil="canEditDeleteDoc"
        :showDeleteBtnFunc="canEditDeleteDoc"
        :showAllDeleteBtn="false"
        :uploadOnTop="true"
        :sourceId="docSourceId"
        :sortingToggle="true"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { computed, getCurrentInstance, unref } from 'vue-demi'
import { useStoreGetters } from '@/store/index'
import EditAttachments from '@/controls/EditAttachments.vue'
import HkldDropzone from '@/controls/HkldDropzone.vue'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import mutationTypes from '@/store/mutation-types'
import InputField from '@/controls/InputField'
import DatePicker from '@/controls/DatePicker'
import ShowNotice from '@/controls/ShowNotice'
import dateUtil from '@/utils/dateUtil'
import { mergePasteData } from '@/utils/clipboard'
import formFullnameMixin from '@/pages/formFullnameMixin'
import { getFullnameSorts } from '@/utils/fullName'
import { disableTenantContractorEdit } from './utils'
import { getRegionalWorkPermitLabels } from './regionalWorkPermitLabels'

export default {
  name: 'WorkerInfo',
  components: {
    DatePicker,
    InputField,
    ShowNotice,
    HkldDropzone,
    EditAttachments,
  },
  mixins: [formFullnameMixin],
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    showOperate: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    let returnData = {}

    const { currentWorkPermitRef } = useStoreGetters('workPermit', ['currentWorkPermit'])
    const workerInfo = computed(() => {
      return lodash.get(unref(currentWorkPermitRef), 'workers', [])
        .map((info) => ({
          ...info,
          // For easier paste handling.
          cardExpiryDate: info.cardExpiryDate ? new Date(info.cardExpiryDate) : '',
        }))
    })
    const parsedWorkInfo = computed(() => {
      return unref(workerInfo).map((info) => ({
        ...info,
        cardExpiryDate: info.cardExpiryDate ? dateUtil.formatDate(info.cardExpiryDate) : '',
      }))
    })
    returnData = {
      ...returnData,
      workerInfo,
      parsedWorkInfo,
    }

    const yesterday = dateUtil.yesterday()
    const onRowPaste = (pasteEvent) => {
      mergePasteData({
        pasteEvent,
        currentData: unref(workerInfo),
        columnKeys: [...getFullnameSorts().map(item => item.value), 'cardNum', 'cardExpiryDate', 'phone', 'remarks'],
        commonInfo: {
          sourceId: unref(currentWorkPermitRef).id,
          projectId: unref(currentWorkPermitRef).projectId,
        },
        callback: (data) => {
          proxy.updateWorkerInfo(data.map((item) => {
            /**
             * @type {Date|string}
             */
            let cardExpiryDate = item.cardExpiryDate
            // Convert pasted date string to Date object
            if (cardExpiryDate && typeof cardExpiryDate === 'string') {
              const originLocale = moment.locale()
              try {
                moment.locale('en')
                const m = moment(cardExpiryDate, 'D-MMM-YY', true)
                if (m.isValid() && m.isAfter(yesterday)) {
                  cardExpiryDate = m.toDate()
                } else {
                  cardExpiryDate = ''
                }
              } finally {
                moment.locale(originLocale)
              }
            }
            return {
              ...item,
              cardExpiryDate,
            }
          }))
        },
      })
    }
    returnData = {
      ...returnData,
      onRowPaste,
      yesterday,
    }

    return returnData
  },
  data () {
    return {
      isEditing: this.isEdit,
      sortingAttr: getFullnameSorts()[0].value,
      reverse: false,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit']),
    ...mapGetters('user', ['isEngineer', 'currentUser']),
    ...mapGetters('regions', ['regionName']),
    NoticeOnWorkerBadgesCN () {
      return this.$t(`termsAndConditions.${this.regionName}.NoticeOnWorkerBadgesCN`)
    },
    docSourceId () {
      return lodash.get(this.currentWorkPermit, 'workerDocSourceId', '')
    },
    NoticeOnWorkerBadgesEN () {
      return this.$t(`termsAndConditions.${this.regionName}.NoticeOnWorkerBadgesEN`)
    },
    workPermitLabels () {
      return getRegionalWorkPermitLabels()
    },
    canEditAttachment () {
      return this.showOperate || (this.isEdit && !disableTenantContractorEdit())
    },
    tableHeaders () {
      return [
        {
          name: '#',
          className: 'index',
        },
        ...getFullnameSorts().map(item => ({
          name: this.$t(`contact.basic.${item.label}`),
          className: [item.label, {'required-flag': this.isEditing}],
          attr: item.value,
        })),
        {
          name: this.workPermitLabels.greenCardNo,
          className: ['greenCardNo', {'required-flag': this.isEditing}],
          attr: 'cardNum',
        },
        {
          name: this.workPermitLabels.greenCardExpiryDate,
          className: ['greenCardExpiryDate'],
          attr: 'cardExpiryDate',
        },
        {
          name: this.$t('workPermit.phoneNum'),
          className: 'phone',
          attr: 'phone',
        },
        {
          name: this.$t('workPermit.remarks'),
          className: 'remarks',
          attr: 'remarks',
        },
      ]
    },
  },
  watch: {
    isEdit (val, oldVal) {
      if (val !== oldVal) {
        this.isEditing = val
      }
    },
  },
  methods: {
    ...mapMutations('workPermit', {
      updateWorkerInfo: mutationTypes.WORKPERMIT_SET_WORKER_INFO,
      updateValueForWorkferInfo: mutationTypes.WORKPERMIT_SET_VALUE_FOR_WORKER_INFO,
    }),
    ...mapActions('workPermit', ['updateWorkerInfoBySourceId', 'getWorkPermitById']),
    addWorkInfo () {
      const workerInfo = lodash.cloneDeep(this.workerInfo)
      const info = {
        firstName: '',
        lastName: '',
        cardNum: '',
        cardExpiryDate: '',
        phone: '',
        remarks: '',
        sourceId: this.currentWorkPermit.id,
        projectId: this.currentWorkPermit.projectId,
      }
      workerInfo.push(info)
      this.updateWorkerInfo(workerInfo)
    },

    updateWorkInfo (index, name, value) {
      const workerInfo = lodash.cloneDeep(this.workerInfo)
      workerInfo[index][name] = value
      this.updateWorkerInfo(workerInfo)
    },
    canEditDeleteDoc (item) {
      return this.currentUser.id === item.createdBy && this.canEditAttachment
    },
    changeEditStatus () {
      this.isEditing = !this.isEditing
      this.$emit('editing:workerInfo', this.isEditing)
    },
    updateField (index, name, value) {
      this.updateValueForWorkferInfo({index, name, value})
    },
    deleteWorker (index) {
      const workerInfo = lodash.cloneDeep(this.workerInfo)
      workerInfo.splice(index, 1)
      this.updateWorkerInfo(workerInfo)
    },
    async save () {
      if (this.validate()) {
        let workers = lodash.cloneDeep(this.currentWorkPermit.workers)
        workers = workers.map(worker => {
          worker['projectId'] = this.currentWorkPermit.projectId
          return worker
        })
        await this.updateWorkerInfoBySourceId({
          sourceId: this.currentWorkPermit.id,
          request: workers,
        })
        await this.getWorkPermitById(this.currentWorkPermit.id)
        formUtils.resetDirty()
        this.changeEditStatus()
      }
    },
    async cancel () {
      this.changeEditStatus()
      formUtils.resetDirty()
      await this.getWorkPermitById(this.currentWorkPermit.id)
    },
    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, `.workerInfo`)
      return fusionInputCheckUtil.isAllFieldsValid(`.workerInfo`)
    },
    sortTable (header) {
      if (this.sortingAttr === header.attr) {
        this.reverse = !this.reverse
      } else {
        this.sortingAttr = header.attr
      }
      const originArray = lodash.cloneDeep(this.workerInfo)
      const sortedArray = lodash.orderBy(originArray, [sortIterator => (sortIterator[this.sortingAttr] || '').toLowerCase()], this.reverse ? 'desc' : 'asc')
      this.updateWorkerInfo(sortedArray)
    },
  },
}
</script>

<style lang='scss'>
.workerInfo {
  .workerInfo_triangle_box {
    @import '~styles/components/hotb-list';
  }
  .bold-font {
    font-weight: bold;
  }
  .bold {
    font-weight: bold;
  }
  input {
    width: 100% !important;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
    tr {
      border: solid 1px #ededed;
      color: #10416c;
    }
  }
  .workerInfo_operate {
    font-size: 0;
    right: 0;
    text-align: right;
    margin: 10px 0;
    button {
      margin-right: 15px;
    }
    button:last-child {
      margin-right: 0;
    }
  }
  .workerInfo_table {
    .remarks {
      position: relative;
      .input-field {
        width: calc(100% - 30px);
      }
    }
    & thead {
      & th {
        cursor: pointer;
        user-select: none;
      }
    }
    td:last-child .fa-times {
      cursor: pointer;
      position: absolute;
      right: 10px;
      top: 15px;
      font-size: 25px;
      color: #c3c3c3;
    }
    th {
      text-transform: uppercase;
    }
    input {
      margin-bottom: 0 !important;
    }
  }
  .workerInfo_addWorker {
    width: 220px;
    text-align: center;
    background: #6e7a91;
    height: 43px;
    border-radius: 23px;
    line-height: 43px;
    color: white;
    font-weight: bold;
    margin: 0 auto;
    cursor: pointer;
  }
  .workerInfo_docs {
    margin-top: 10px
  }
  .fusion-input-check-is-invalid-input {
    border-color: #B5B5B5 !important;
  }
}
</style>
